import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { romeDateKey } from "@/lib/format";

const WD = ["Lu", "Ma", "Me", "Gi", "Ve", "Sa", "Do"];

export function MonthCalendar({ treatmentId, staffId, value, onChange, maxDays = 60 }) {
  const today = romeDateKey();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [days, setDays] = useState(null);
  useEffect(() => {
    setDays(null);
    api.get("/public/booking/days", { params: { treatment_id: treatmentId, month, ...(staffId ? { staff_id: staffId } : {}) } })
      .then((r) => setDays(new Set(r.data.days))).catch(() => setDays(new Set()));
  }, [treatmentId, staffId, month]);
  const cells = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(Date.UTC(y, m - 1, 1));
    const offset = (first.getUTCDay() + 6) % 7;
    const count = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return [...Array(offset).fill(null), ...Array.from({ length: count }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`)];
  }, [month]);
  const shift = (n) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    setMonth(d.toISOString().slice(0, 7));
  };
  const label = new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-01T12:00:00Z`));
  const maxMonth = new Date(Date.now() + maxDays * 864e5).toISOString().slice(0, 7);
  return (
    <div className="card-soft p-5 sm:p-7" data-testid="booking-calendar">
      <div className="mb-5 flex items-center justify-between">
        <button type="button" onClick={() => shift(-1)} disabled={month <= today.slice(0, 7)} aria-label="Mese precedente" className="rounded-full p-2 hover:bg-[var(--ivory)] disabled:opacity-30" data-testid="calendar-prev"><ChevronLeft /></button>
        <p className="font-serif text-2xl capitalize" aria-live="polite">{label}</p>
        <button type="button" onClick={() => shift(1)} disabled={month >= maxMonth} aria-label="Mese successivo" className="rounded-full p-2 hover:bg-[var(--ivory)] disabled:opacity-30" data-testid="calendar-next"><ChevronRight /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs text-[var(--muted-ink)]">{WD.map((d) => <span key={d} className="py-2">{d}</span>)}</div>
      <div className="grid grid-cols-7 gap-1" role="grid" aria-busy={days === null}>
        {cells.map((d, i) => {
          if (!d) return <span key={`e${i}`} />;
          const avail = days?.has(d);
          const sel = value === d;
          return (
            <button key={d} type="button" disabled={!avail} onClick={() => onChange(d)} aria-pressed={sel} aria-label={`${d}${avail ? " disponibile" : " non disponibile"}`}
              className={`aspect-square rounded-full text-sm transition-colors ${sel ? "bg-[var(--petrol)] text-white" : avail ? "bg-[var(--powder-light)] font-medium hover:bg-[var(--powder)]" : "text-muted-ink/50"}`} data-testid={`calendar-day-${d}`}>
              {Number(d.slice(8))}
            </button>
          );
        })}
      </div>
      {days && days.size === 0 && <p className="mt-4 text-sm text-[var(--muted-ink)]" data-testid="calendar-empty">Nessuna disponibilità in questo mese.</p>}
    </div>
  );
}

export function SlotPicker({ treatmentId, staffId, date, value, onChange }) {
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    if (!date) return;
    setSlots(null);
    api.get("/public/booking/slots", { params: { treatment_id: treatmentId, date, ...(staffId ? { staff_id: staffId } : {}) } })
      .then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [treatmentId, staffId, date]);
  if (!date) return <p className="text-sm text-[var(--muted-ink)]">Seleziona un giorno per vedere gli orari.</p>;
  if (!slots) return <p className="text-sm">Caricamento orari…</p>;
  if (!slots.length) return <p className="text-sm" data-testid="slots-empty">Nessun orario disponibile.</p>;
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" role="group" aria-label="Orari disponibili" data-testid="slot-list">
      {slots.map((s) => (
        <button key={s.start} type="button" onClick={() => onChange(s)} aria-pressed={value?.start === s.start}
          className={`rounded-full border px-3 py-2.5 text-sm transition-colors ${value?.start === s.start ? "border-[var(--petrol)] bg-[var(--petrol)] text-white" : "border-[var(--line)] bg-white hover:border-[var(--brass)]"}`} data-testid={`slot-${s.time}`}>
          {s.time}
        </button>
      ))}
    </div>
  );
}
